import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
    FormRowComponent,
    InstructionsComponent,
    InstructionsWithFieldsContainerComponent,
    TypeaheadSearchComponent,
} from '@r1-compass-library/components';
import { rxEffect } from 'ngxtension/rx-effect';
import { DivisionSalesforceTargetsFormService } from '../division-salesforce-targets-form.service';
import { SalesforceObjectMetadata } from '@r1-compass-library/models/salesforce/salesforce-metadata.model';
import { combineLatest, filter } from 'rxjs';
import { SalesforceListFieldResponse } from '@r1-compass-library/models';

@Component({
    selector: 'r1-compass-app-salesforce',
    imports: [
        InstructionsWithFieldsContainerComponent,
        InstructionsComponent,
        FormRowComponent,
        TypeaheadSearchComponent,
        ReactiveFormsModule,
    ],
    template: `
        <r1-compass-lib-instructions-with-fields-container>
            <r1-compass-lib-instructions instructions headerText="Salesforce">
                Select the field to use as the Recommendation Unique ID for Importer plays. Then, select the objects in
                Salesforce to be used as the Play and Recommendation objects.
            </r1-compass-lib-instructions>
            @let form = formService.divisionSalesforceTargetsForm;
            <form formArea [formGroup]="form">
                <r1-compass-lib-form-row name="Importer Recommendation Unique ID *">
                    <r1-compass-lib-typeahead-search
                        [valueFormControl]="form.controls.salesforceUniqueRecommendationFieldTargetName"
                        [displayFormControl]="salesforceUniqueRecommendationFieldTargetNameDisplayControl"
                        [resultDisplayPropertyPath]="'name'"
                        [resultValuePropertyPath]="'name'"
                        [disabled]="false"
                        [searchResults]="salesforceUniqueRecommendationFieldTargetNameResults()"
                        (search)="salesforceUniqueRecommendationFieldTargetNameSearchState.set($event)"
                        (resultSelection)="salesforceUniqueRecommendationFieldTargetNameSearchState.set('')" />
                </r1-compass-lib-form-row>
                <r1-compass-lib-form-row name="Salesforce Play Object *">
                    <r1-compass-lib-typeahead-search
                        [valueFormControl]="form.controls.salesforcePlayObject"
                        [displayFormControl]="salesforcePlayObjectDisplayControl"
                        [resultDisplayPropertyPath]="'name'"
                        [resultValuePropertyPath]="'name'"
                        [disabled]="false"
                        [searchResults]="salesforcePlayObjectResults()"
                        (search)="playObjectSearchState.set($event)"
                        (resultSelection)="playObjectSearchState.set('')" />
                </r1-compass-lib-form-row>
                <r1-compass-lib-form-row name="Salesforce Recommendation Object *">
                    <r1-compass-lib-typeahead-search
                        [valueFormControl]="form.controls.salesforceRecommendationObject"
                        [displayFormControl]="salesforceRecommendationObjectDisplayControl"
                        [resultDisplayPropertyPath]="'name'"
                        [resultValuePropertyPath]="'name'"
                        [disabled]="false"
                        [searchResults]="salesforceRecommendationObjectResults()"
                        (search)="recommendationObjectSearchState.set($event)"
                        (resultSelection)="recommendationObjectSearchState.set('')" />
                </r1-compass-lib-form-row>
            </form>
        </r1-compass-lib-instructions-with-fields-container>
    `,
})
export class SalesforceComponent {
    protected readonly formService = inject(DivisionSalesforceTargetsFormService);

    private formState = this.formService.salesforceTargetsFormState;

    // Typeahead search states
    protected salesforceUniqueRecommendationFieldTargetNameSearchState = signal('');
    protected playObjectSearchState = signal('');
    protected recommendationObjectSearchState = signal('');

    // Typeahead display controls
    protected salesforceUniqueRecommendationFieldTargetNameDisplayControl = new FormControl('');
    protected salesforcePlayObjectDisplayControl = new FormControl('');
    protected salesforceRecommendationObjectDisplayControl = new FormControl('');

    protected salesforceUniqueRecommendationFieldTargetNameResults = computed<SalesforceObjectMetadata[]>(() => {
        const recommendationFieldOptions = this.formState.recommendationFieldsOptions();
        if (recommendationFieldOptions) {
            return this.narrowSalesforceObjectOptions(
                this.salesforceUniqueRecommendationFieldTargetNameSearchState(),
                recommendationFieldOptions.fields
            );
        } else {
            return [];
        }
    });

    protected salesforcePlayObjectResults = computed<SalesforceObjectMetadata[]>(() =>
        this.narrowSalesforceObjectOptions(this.playObjectSearchState(), this.formState.salesforceObjectOptions())
    );

    protected salesforceRecommendationObjectResults = computed<SalesforceObjectMetadata[]>(() =>
        this.narrowSalesforceObjectOptions(
            this.recommendationObjectSearchState(),
            this.formState.salesforceObjectOptions()
        )
    );

    constructor() {
        // Set typeaheads/display forms
        rxEffect(
            combineLatest([
                this.formState.salesforceObjectOptions$,
                this.formState.recommendationFieldsOptions$.pipe(filter(options => !!options)),
            ]),
            ([salesforceObjectOptions, recommendationFieldsOptions]) => {
                this.setTypeaheads(salesforceObjectOptions, recommendationFieldsOptions);
            }
        );
    }

    private setTypeaheads(
        salesforceObjectOptions: SalesforceObjectMetadata[],
        recommendationFieldsOptions: SalesforceListFieldResponse
    ) {
        salesforceObjectOptions.find(salesforceOption => {
            if (salesforceOption.name === this.formState.value().salesforcePlayObject) {
                this.salesforcePlayObjectDisplayControl.setValue(salesforceOption?.name ?? '');
                this.playObjectSearchState.set(salesforceOption.name);
            }
            if (salesforceOption.name === this.formState.value().salesforceRecommendationObject) {
                this.salesforceRecommendationObjectDisplayControl.setValue(salesforceOption?.name ?? '');
                this.recommendationObjectSearchState.set(salesforceOption.name);
            }
        });
        recommendationFieldsOptions.fields.find(recOption => {
            if (recOption.name === this.formState.value().salesforceUniqueRecommendationFieldTargetName) {
                this.salesforceUniqueRecommendationFieldTargetNameDisplayControl.setValue(recOption?.name ?? '');
                this.salesforceUniqueRecommendationFieldTargetNameSearchState.set(recOption.name);
            }
        });
    }

    private narrowSalesforceObjectOptions(searchString: string, salesforceObjectOptions: SalesforceObjectMetadata[]) {
        return searchString !== ''
            ? salesforceObjectOptions
                  .filter(opts => opts.name.toLowerCase().includes(searchString.toLowerCase()))
                  .sort(function (a, b) {
                      return a.name.localeCompare(b.name);
                  })
            : [];
    }
}
